import {
    createSelector,
} from '@reduxjs/toolkit';
import i18n from '~shared/utils/i18n';

import {
    selectShowModal,
    selectUiMode,
    selectDisplayFullslip,
} from '~Betslip/selectors/uiSelectors';
import { isSpanishWageringLimitError } from '~Betslip/utils/errorMappings';
import { ACCA_BOOST_TYPE_ID } from '~shared/utils/constants';

export const selectBetReceipt = state => state.betslip.betReceipt;

export const selectBets = createSelector(
    selectBetReceipt,
    (betReceipt) => (betReceipt ? betReceipt.bets : [])
);

export const selectBetReceiptIds = createSelector(
    selectBets,
    bets => {
        const allIds = bets.reduce((acc, bet) => {
            return [
                ...acc,
                ...bet.selection_ids,
            ];
        }, []);

        return [...new Set(allIds.map(String))];
    }
);

export const selectCountReceiptSelections = createSelector(
    selectBetReceiptIds,
    ids => ids.length
);

/**
 * Returns combinations across all bets in a receipt
 */
export const selectReceiptBetCombinations = createSelector(
    selectBets,
    bets => {
        const comboIds = bets.reduce(
            (allCombinations, bet) => {
                const { combination_selections = {} } = bet;

                return { ...allCombinations, ...combination_selections };
            },
            {},
        );

        return comboIds;
    },
);

export const selectBetsErrors = ({ betslip: { betReceipt }}) => {
    return betReceipt ? betReceipt.errors : [];
};

export const selectExtraErrorData = ({ betslip: { betReceipt }}) => {
    return betReceipt?.extraErrorData || {};
};

export const selectErrno = ({ betslip: { betReceipt }}) => {
    return betReceipt?.errno || 0;
};

export const selectBetTotalStake = ({ betslip: { betReceipt }}) => {
    return betReceipt ? betReceipt.total_stake : 0;
};

export const selectBetTotalReturns = ({ betslip: { betReceipt = {} }}) => (
    betReceipt.total_potential_returns ?? i18n.t('betslip.na')
);

export const selectTabPlacedFrom = ({ betslip: { betReceipt }}) => {
    return betReceipt ? betReceipt.placedFrom : '';
};

export const selectBetReceiptExists = createSelector(
    selectBetReceipt,
    (betReceipt) => Boolean(betReceipt !== null)
);

export const selectShowBetReceipt = createSelector(
    [
        selectBetReceiptExists,
        selectShowModal,
    ],
    (betReceiptExists, modalIsShown) => {
        return betReceiptExists && !modalIsShown;
    },
);

export const selectShowBabyBetReceipt = createSelector(
    [
        selectBetReceiptExists,
        selectShowModal,
        selectUiMode,
        selectDisplayFullslip,
    ],
    (betReceiptExists, modalIsShown, uiMode, displayFullslip) => {
        return betReceiptExists && !modalIsShown && uiMode === 'baby' && !displayFullslip;
    },
);

export const selectBetTotalBoostedReturns = createSelector(
    selectBets,
    bets => {
        const hasBonuses = bets.some(bet => (bet?.bonuses || []).length > 0);

        if (!hasBonuses) {
            return null;
        }

        return bets.reduce((total, bet) => {
            const {
                potential_returns = 0,
                bonuses = [],
            } = bet || {};

            const accaBoostBonus = bonuses.find(bonus => bonus?.type_id === ACCA_BOOST_TYPE_ID);
            const potentialReturns = Number(potential_returns) || 0;
            const bonusAmount = Number(accaBoostBonus?.max_amount) || 0;

            return total + potentialReturns + bonusAmount;
        }, 0);
    }
);

export const selectFirstBetAccaBoost = createSelector(
    selectBets,
    bets => (bets[0]?.bonuses || []).find(b => b?.type_id === ACCA_BOOST_TYPE_ID) || null,
);

export const selectIsOnlyBetSpanishWageringError = createSelector(
    selectBets,
    selectBetsErrors,
    (bets = [], errors) => bets.length === 1 &&
        errors.some(error => isSpanishWageringLimitError(error?.id)),
);

export const selectIsOnlyBetResubmitted = createSelector(
    selectBets,
    selectBetsErrors,
    (bets = [], errors = []) => bets.length === 1
        && !errors.length
        && Boolean(bets[0].was_resubmitted)
        && Boolean(bets[0].stake)
        && Boolean(bets[0].original_stake)
);

export const selectFirstBetOriginalStake = createSelector(
    selectBets,
    (bets = []) => Number(bets[0]?.original_stake) || null,
);

export const selectFirstBetWasResubmitted = createSelector(
    selectBets,
    (bets = []) => Boolean(bets[0]?.was_resubmitted),
);
